import React from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material'
import { ServicesTemplateStructure, ServicesTemplateSection, ServicesTemplateField } from '../../types'

// Extend the field type for runtime use (template can have any type string)
type FieldType = ServicesTemplateField['type'] | string

interface ServicesFormRendererProps {
  template: ServicesTemplateStructure
  data: Record<string, any>
  onChange: (fieldId: string, value: any) => void
  readOnly?: boolean
}

export const ServicesFormRenderer: React.FC<ServicesFormRendererProps> = ({
  template,
  data,
  onChange,
  readOnly = false,
}) => {
  // Render a single field based on its type
  const renderField = (field: ServicesTemplateField) => {
    const value = data[field.id] ?? ''
    const fieldType = field.type as FieldType
    
    switch (fieldType) {
      case 'text':
      case 'email':
      case 'tel':
        return (
          <TextField
            fullWidth
            size="small"
            type={field.type}
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            disabled={readOnly}
            InputProps={{
              endAdornment: field.explanation ? (
                <InputAdornment position="end">
                  <Tooltip title={field.explanation} arrow>
                    <IconButton size="small" tabIndex={-1}>
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
          />
        )

      case 'textarea':
        return (
          <TextField
            fullWidth
            size="small"
            multiline
            rows={3}
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            disabled={readOnly}
            helperText={field.explanation}
          />
        )

      case 'number':
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => onChange(field.id, e.target.value ? Number(e.target.value) : '')}
            disabled={readOnly}
            InputProps={{
              endAdornment: field.unit ? (
                <InputAdornment position="end">{field.unit}</InputAdornment>
              ) : field.explanation ? (
                <InputAdornment position="end">
                  <Tooltip title={field.explanation} arrow>
                    <IconButton size="small" tabIndex={-1}>
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
          />
        )

      case 'price':
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            label={field.label}
            placeholder={field.placeholder || '0'}
            value={value}
            onChange={(e) => onChange(field.id, e.target.value ? Number(e.target.value) : '')}
            disabled={readOnly}
            InputProps={{
              endAdornment: <InputAdornment position="end">kr.</InputAdornment>,
            }}
            helperText={field.explanation}
          />
        )

      case 'checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(value)}
                onChange={(e) => onChange(field.id, e.target.checked)}
                disabled={readOnly}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {field.label}
                {field.explanation && (
                  <Tooltip title={field.explanation} arrow>
                    <HelpIcon fontSize="small" color="action" />
                  </Tooltip>
                )}
              </Box>
            }
          />
        )

      case 'checkboxGroup':
        const selectedItems = Array.isArray(value) ? value : []
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {field.label}
              {field.explanation && (
                <Tooltip title={field.explanation} arrow>
                  <HelpIcon fontSize="small" color="action" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                </Tooltip>
              )}
            </Typography>
            <FormGroup row>
              {field.options?.map((option: any) => (
                <FormControlLabel
                  key={typeof option === 'string' ? option : option.id}
                  control={
                    <Checkbox
                      checked={selectedItems.includes(typeof option === 'string' ? option : option.id)}
                      onChange={(e) => {
                        const optionId = typeof option === 'string' ? option : option.id
                        if (e.target.checked) {
                          onChange(field.id, [...selectedItems, optionId])
                        } else {
                          onChange(field.id, selectedItems.filter((item: string) => item !== optionId))
                        }
                      }}
                      disabled={readOnly}
                      size="small"
                    />
                  }
                  label={typeof option === 'string' ? option : option.label}
                />
              ))}
            </FormGroup>
          </Box>
        )

      case 'select':
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => onChange(field.id, e.target.value)}
              label={field.label}
              disabled={readOnly}
            >
              <MenuItem value="">
                <em>Vælg...</em>
              </MenuItem>
              {field.options?.map((option: any) => (
                <MenuItem 
                  key={typeof option === 'string' ? option : option.id} 
                  value={typeof option === 'string' ? option : option.id}
                >
                  {typeof option === 'string' ? option : option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )

      case 'religionOptions':
        // Special handling for religion types
        const religions = [
          { id: 'christian', label: 'Kristen begravelse' },
          { id: 'catholic', label: 'Katolsk begravelse' },
          { id: 'jewish', label: 'Jødisk begravelse' },
          { id: 'muslim', label: 'Muslimsk begravelse' },
          { id: 'buddhist', label: 'Buddhistisk begravelse' },
          { id: 'hindu', label: 'Hinduistisk begravelse' },
          { id: 'humanist', label: 'Humanistisk / Borgerlig' },
          { id: 'spiritual', label: 'Spirituel begravelse' },
        ]
        const selectedReligions = Array.isArray(value) ? value : []
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {field.label}
            </Typography>
            <FormGroup>
              {religions.map((religion) => (
                <FormControlLabel
                  key={religion.id}
                  control={
                    <Checkbox
                      checked={selectedReligions.includes(religion.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onChange(field.id, [...selectedReligions, religion.id])
                        } else {
                          onChange(field.id, selectedReligions.filter((r: string) => r !== religion.id))
                        }
                      }}
                      disabled={readOnly}
                      size="small"
                    />
                  }
                  label={religion.label}
                />
              ))}
            </FormGroup>
          </Box>
        )

      case 'serviceList':
        // Simple layout: label + price field
        // If price is filled in, they offer it. Empty = not offered
        // Data is stored as: { service_id: price }
        const servicesData = (typeof value === 'object' && value !== null) ? value : {}
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {field.options?.map((option: any, idx: number) => {
              const serviceId = typeof option === 'string' ? option : option.id
              const serviceLabel = typeof option === 'string' ? option : option.label
              const price = servicesData[serviceId] || ''
              
              return (
                <Box 
                  key={serviceId}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: idx % 2 === 0 ? 'grey.50' : 'background.paper',
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {serviceLabel}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Pris"
                    value={price}
                    onChange={(e) => {
                      const newServicesData = {
                        ...servicesData,
                        [serviceId]: e.target.value ? Number(e.target.value) : ''
                      }
                      onChange(field.id, newServicesData)
                    }}
                    disabled={readOnly}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">kr.</InputAdornment>,
                    }}
                    sx={{ width: 130 }}
                  />
                </Box>
              )
            })}
          </Box>
        )

      case 'productLevels':
        // Simple layout for coffins/urns: just label + description + price
        // If price is filled in, they offer it. Empty = not offered
        // Data is stored as: { level_id: price }
        const productData = (typeof value === 'object' && value !== null) ? value : {}
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {field.options?.map((option: any, idx: number) => {
              const levelId = option.id
              const price = productData[levelId] || ''
              
              return (
                <Box 
                  key={levelId}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: idx % 2 === 0 ? 'grey.50' : 'background.paper',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {option.label}
                    </Typography>
                    {option.description && (
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Pris"
                    value={price}
                    onChange={(e) => {
                      const newProductData = {
                        ...productData,
                        [levelId]: e.target.value ? Number(e.target.value) : ''
                      }
                      onChange(field.id, newProductData)
                    }}
                    disabled={readOnly}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">kr.</InputAdornment>,
                    }}
                    sx={{ width: 140 }}
                  />
                </Box>
              )
            })}
          </Box>
        )

      case 'packagePrice':
        // Package with included items list and price field
        // Data is stored as: { price: number }
        const packageData = (typeof value === 'object' && value !== null) ? value : { price: '' }
        const includedItems = (field as any).included || []
        return (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {field.label}
            </Typography>
            
            {includedItems.length > 0 && (
              <Box sx={{ mb: 2, pl: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Inkluderet i pakken:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {includedItems.map((item: string, idx: number) => (
                    <Typography component="li" variant="body2" key={idx} sx={{ color: 'text.secondary' }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Samlet pakkepris (inkl. moms)"
              value={packageData.price || ''}
              onChange={(e) => {
                onChange(field.id, { 
                  ...packageData, 
                  price: e.target.value ? Number(e.target.value) : '' 
                })
              }}
              disabled={readOnly}
              InputProps={{
                endAdornment: <InputAdornment position="end">kr.</InputAdornment>,
              }}
            />
          </Paper>
        )

      default:
        return (
          <TextField
            fullWidth
            size="small"
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            disabled={readOnly}
            helperText={`Unknown type: ${field.type}`}
          />
        )
    }
  }

  // Render a section with its fields
  const renderSection = (section: ServicesTemplateSection, index: number) => {
    return (
      <Accordion 
        key={section.id} 
        defaultExpanded={true} // All sections expanded by default
        sx={{ 
          '&:before': { display: 'none' },
          boxShadow: 1,
          '&.Mui-expanded': { margin: '8px 0' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            bgcolor: 'grey.50',
            '&:hover': { bgcolor: 'grey.100' },
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {section.title}
            </Typography>
            {section.description && (
              <Typography variant="caption" color="text.secondary">
                {section.description}
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {section.fields.map((field) => (
              <Box key={field.id}>
                {renderField(field)}
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    )
  }

  if (!template?.sections?.length) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Ingen service-skabelon tilgængelig
        </Typography>
      </Paper>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {template.sections.map((section, index) => renderSection(section, index))}
    </Box>
  )
}

export default ServicesFormRenderer

